import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { sourcingDetails } from 'services/cart/types';
import { v4 as uuidv4 } from 'uuid';
import { AvailabilityLine } from '../availability-line';
import {
  CatButton,
  CatDrawer,
  CatHeading,
  CatPopover
} from 'components/blocks-components';
import {
  Conditional,
  DrawerBody,
  DrawerHeader,
  useDrawer
} from '@cat-ecom/pcc-components';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import {
  getMonthAndDayFormatted,
  getSourceDate,
  getSourceDetails
} from '../utils';
import { AvailabilityQuantity } from '../availability-quantity';
import { AvailabilityByDateLine } from '../types';
import ContactDealerPopoverContent from 'components/contact-your-dealer/contact-dealer-popover-content/contact-dealer-popover-content';
import { LocationAddress } from 'services/location/types';
import { useProductDetailPageContext } from 'app/[lang]/[esite]/(core)/product/[product]/context/product-detail';
import ContactDealerDrawer from '../contact-dealer-drawer/contact-dealer-drawer';
import { Store } from 'components/availability-wrapper/dealer-other-branches/types';
import styles from '../product-availability.module.scss';
export interface ProductAvailabilityByDateProps {
  sourcingDetails: sourcingDetails[];
  isCompact?: boolean;
  shouldWrap?: boolean;
  address?: LocationAddress;
  locale?: string;
  closeAvailabilityDrawer?: () => void;
  openInDrawer?: boolean;
  store?: Store;
  isPccReleaseRemanReplacement?: boolean;
  partsDiagramMobileView?: boolean;
  isPrint?: boolean;
}

export function ProductAvailabilityByDate({
  sourcingDetails,
  isCompact = false,
  shouldWrap = false,
  address,
  locale,
  closeAvailabilityDrawer,
  openInDrawer = false,
  store,
  isPccReleaseRemanReplacement = false,
  partsDiagramMobileView = false,
  isPrint = false
}: Readonly<ProductAvailabilityByDateProps>) {
  const t = useTranslations();
  const { isPCCHeadlessAvailabilityFlag } = useProductDetailPageContext();
  const isQuantityGreaterThanOne = (quantity: number) => quantity > 1;
  const contactDealerDrawer = useDrawer({
    id: 'contact-dealer-drawer',
    size: 'lg'
  });

  const { drawerProps, openDrawer } = useDrawer();
  const getDateText = useCallback(
    (quantity, date, isLast = false) => {
      const { month, day } = getMonthAndDayFormatted(date);

      const styledQuantity = <span className="fw-bold">{quantity}</span>;

      if (
        isLast &&
        (!isPCCHeadlessAvailabilityFlag || isQuantityGreaterThanOne(quantity))
      ) {
        return replaceTokensWithComponents(
          t('AVAIL_STANDARD_ALL_PICKUP', { 0: '{0}', 1: month, 2: day }),
          styledQuantity
        );
      }

      return replaceTokensWithComponents(
        t('AVAIL_STANDARD_SOME_PICKUP', { 0: '{0}', 1: month, 2: day }),
        styledQuantity
      );
    },
    [isPCCHeadlessAvailabilityFlag, t]
  );

  const availabilityLines: AvailabilityByDateLine[] = useMemo(() => {
    const sourcingMapped: AvailabilityByDateLine[] = [];

    sourcingDetails?.forEach(
      ({
        availStoreList,
        availableByDate,
        sequence,
        availabilityMessage,
        quantityAvailable
      }) => {
        const { isContactDealer, storeName, message, quantity } =
          getSourceDetails(
            availStoreList,
            quantityAvailable,
            availabilityMessage,
            sequence
          );
        const isInStock =
          !isContactDealer &&
          storeName?.toLowerCase() === t('MO_SHIP_IN_STOCK').toLowerCase(); // Extracting from message because there is no field for inStock
        if (isInStock || isContactDealer || (storeName && !availableByDate)) {
          return sourcingMapped.push({
            message: message,
            quantity: quantity,
            isInStock: isInStock,
            isContactDealer: isContactDealer
          });
        }
        const sourceDate = getSourceDate(availableByDate);

        if (sourceDate) {
          // Check if we already have sourceByDateMapped
          const currentSourceByDate = sourcingMapped.find(
            (source) => source?.date
          );
          if (currentSourceByDate) {
            currentSourceByDate.quantity += quantity;
            // Use the furthest date to be shipped
            if (sourceDate > currentSourceByDate.date) {
              currentSourceByDate.date = sourceDate;
            }
          } else {
            sourcingMapped.push({
              date: sourceDate,
              quantity: quantity,
              isInStock: false,
              isContactDealer: false
            });
          }
        }
      }
    );

    // From sourcingMapped, check if we have an entry that isInStock and
    // add their quantity to the quantity of the entry that has date property
    sourcingMapped.reduce(
      (acc, source) => {
        if (source.isInStock && !acc.inStockFound) {
          const dateEntry = sourcingMapped.find((item) => item.date);
          if (dateEntry) {
            dateEntry.quantity += source.quantity;
          }
          return { inStockFound: true };
        }
        return acc;
      },
      { inStockFound: false }
    );

    return sourcingMapped;
  }, [sourcingDetails, t]);

  return (
    <div>
      {availabilityLines?.map(
        (
          { date, message, quantity, isContactDealer, isInStock },
          i,
          { length }
        ) => (
          <AvailabilityLine
            key={uuidv4()}
            isCompact={isCompact}
            shouldWrap={shouldWrap}
            isPrint={isPrint}
          >
            {isContactDealer || isInStock || (message && !date) ? (
              <>
                <AvailabilityQuantity
                  isInStock={isInStock}
                >{`${quantity}`}</AvailabilityQuantity>
                {isContactDealer &&
                  (!openInDrawer ? (
                    <CatPopover
                      behavior="responsive"
                      headingVariant="title-sm"
                      heading={t('FOOTER_CONTACT_US')}
                      position="bottom"
                    >
                      <CatButton
                        variant="inline"
                        {...(isCompact && { size: 'sm' })}
                        slot="trigger"
                        className={
                          styles['product-availability__no-print-hyperlink']
                        }
                      >
                        {message || t('CONTACT_YOUR_DEALER')}
                      </CatButton>
                      <ContactDealerPopoverContent
                        dealerAddress={address}
                        locale={locale}
                      />
                    </CatPopover>
                  ) : (
                    <Conditional
                      fallback={
                        <>
                          <CatButton
                            variant="inline"
                            {...(isCompact && { size: 'sm' })}
                            onClick={() =>
                              contactDealerDrawer?.openDrawer(null, true)
                            }
                            className={
                              styles['product-availability__no-print-hyperlink']
                            }
                          >
                            {message || t('CONTACT_YOUR_DEALER')}
                          </CatButton>
                          <ContactDealerDrawer
                            contactDealerDrawer={contactDealerDrawer}
                            closeAvailabilityDrawer={closeAvailabilityDrawer}
                            store={store}
                            locale={locale}
                          />
                        </>
                      }
                      test={
                        isPccReleaseRemanReplacement || partsDiagramMobileView
                      }
                    >
                      <CatButton
                        variant="inline"
                        {...(isCompact && { size: 'sm' })}
                        onClick={openDrawer}
                        data-testid="CONTACT_YOUR_DEALER_BUTTON"
                        className={
                          styles['product-availability__no-print-hyperlink']
                        }
                      >
                        {message || t('CONTACT_YOUR_DEALER')}
                      </CatButton>
                      <CatDrawer
                        {...drawerProps}
                        orientation="bottom"
                        data-testid="contact-dealer-drawer"
                        className={
                          styles['product-availability__compact-drawer']
                        }
                        verticalMaxHeight="large"
                      >
                        <DrawerHeader className="d-flex justify-content-between">
                          <CatHeading variant="title-sm">
                            {t('CONTACT_YOUR_DEALER')}
                          </CatHeading>
                        </DrawerHeader>
                        <DrawerBody>
                          <ContactDealerPopoverContent
                            dealerAddress={address}
                            locale={locale}
                          />
                        </DrawerBody>
                      </CatDrawer>
                    </Conditional>
                  ))}
                {!isContactDealer && message}
              </>
            ) : (
              !!date && (
                <span className="px-1">
                  {getDateText(quantity, date, i === length - 1)}
                </span>
              )
            )}
          </AvailabilityLine>
        )
      )}
    </div>
  );
}
